import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Calendar, MapPin, Clock, Heart, Loader2 } from 'lucide-react';

const BACKEND_URL = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL || "");

/**
 * Public page that lets a participant accept or decline a promoted waiting-list spot.
 * Route: /buchung/warteliste/:token/:action  (action = "annehmen" | "ablehnen" | undefined → choice)
 */
export default function WaitingListResponsePage() {
    const { token, action } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [info, setInfo] = useState(null);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null); // 'accepted' | 'declined' | 'expired'

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${BACKEND_URL}/api/bookings/waiting-list/info/${token}`);
                if (!cancelled) {
                    setInfo(res.data);
                    if (res.data?.expired) {
                        setDone('expired');
                    } else if (action === 'annehmen') {
                        await doAccept();
                    } else if (action === 'ablehnen') {
                        await doDecline();
                    }
                }
            } catch (e) {
                if (!cancelled) setError(e.response?.data?.detail || 'Link ungültig oder bereits eingelöst');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const doAccept = async () => {
        setSubmitting(true);
        try {
            await axios.post(`${BACKEND_URL}/api/bookings/waiting-list/${token}/accept`);
            setDone('accepted');
            toast.success('Buchung bestätigt – willkommen!');
        } catch (e) {
            const msg = e.response?.data?.detail || 'Fehler beim Bestätigen';
            setError(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const doDecline = async () => {
        setSubmitting(true);
        try {
            await axios.post(`${BACKEND_URL}/api/bookings/waiting-list/${token}/decline`);
            setDone('declined');
            toast.success('Sie haben den Platz abgelehnt');
        } catch (e) {
            const msg = e.response?.data?.detail || 'Fehler beim Ablehnen';
            setError(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <header className="bg-secondary">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 flex items-center gap-3">
                    <div className="p-2.5 bg-primary rounded-xl">
                        <Heart className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-white">BGPR-Akademie</h1>
                        <p className="text-xs text-gray-400">Warteliste-Antwort</p>
                    </div>
                </div>
            </header>

            <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-12">
                <div className="bg-white rounded-2xl border border-gray-100 p-8 sm:p-10" data-testid="waiting-list-response-card">
                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : error && !done ? (
                        <div className="text-center py-8" data-testid="waiting-list-error">
                            <XCircle className="h-12 w-12 mx-auto mb-4 text-rose-500" />
                            <h2 className="text-xl font-bold text-gray-900 mb-2">Link nicht gültig</h2>
                            <p className="text-sm text-gray-600 mb-6">{error}</p>
                            <Link to="/seminare" className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary/80 transition-colors">
                                Zur Seminar-Übersicht
                            </Link>
                        </div>
                    ) : done === 'accepted' ? (
                        <div className="text-center py-8" data-testid="waiting-list-accepted">
                            <CheckCircle2 className="h-14 w-14 mx-auto mb-4 text-emerald-500" />
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">Platz bestätigt!</h2>
                            <p className="text-sm text-gray-600 mb-6">
                                Vielen Dank – Ihre Buchung ist nun verbindlich bestätigt.
                                Sie erhalten in Kürze eine Bestätigungsmail mit allen Details.
                            </p>
                            <Link to="/portal" className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary/80 transition-colors">
                                Zu Mein Bereich
                            </Link>
                        </div>
                    ) : done === 'declined' ? (
                        <div className="text-center py-8" data-testid="waiting-list-declined">
                            <XCircle className="h-14 w-14 mx-auto mb-4 text-gray-400" />
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">Platz abgelehnt</h2>
                            <p className="text-sm text-gray-600 mb-6">
                                Sie haben den frei gewordenen Platz abgelehnt.
                                Vielen Dank für Ihre Rückmeldung – wir geben den Platz an den nächsten Teilnehmer weiter.
                            </p>
                            <Link to="/seminare" className="inline-flex items-center gap-1.5 px-4 py-2 border border-gray-200 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors">
                                Zur Seminar-Übersicht
                            </Link>
                        </div>
                    ) : done === 'expired' ? (
                        <div className="text-center py-8" data-testid="waiting-list-expired">
                            <XCircle className="h-12 w-12 mx-auto mb-4 text-amber-500" />
                            <h2 className="text-xl font-bold text-gray-900 mb-2">Frist abgelaufen</h2>
                            <p className="text-sm text-gray-600 mb-6">
                                Leider ist die 48-Stunden-Frist zum Bestätigen Ihres Wartelisten-Platzes abgelaufen.
                                Der Platz wurde an den nächsten Teilnehmer weitergegeben.
                            </p>
                            <Link to="/seminare" className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary/80 transition-colors">
                                Zur Seminar-Übersicht
                            </Link>
                        </div>
                    ) : info ? (
                        <>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">Ein Platz ist frei!</h2>
                            <p className="text-sm text-gray-600 mb-6">
                                Hallo {info.participant_name || ''} – für das folgende Seminar ist soeben ein Platz frei geworden.
                                Bitte bestätigen oder lehnen Sie den Platz innerhalb der angegebenen Frist ab.
                            </p>
                            {info.seminar && (
                                <div className="bg-gray-50 rounded-xl p-5 mb-6">
                                    <h3 className="font-bold text-gray-900 mb-3">{info.seminar.title}</h3>
                                    <div className="space-y-2 text-sm text-gray-600">
                                        {info.seminar.course_number && (
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono text-xs text-gray-500">{info.seminar.course_number}</span>
                                            </div>
                                        )}
                                        {info.seminar.date && (
                                            <div className="flex items-center gap-2">
                                                <Calendar className="h-4 w-4 text-gray-400" />
                                                <span>{info.seminar.date}</span>
                                            </div>
                                        )}
                                        {info.seminar.location && (
                                            <div className="flex items-center gap-2">
                                                <MapPin className="h-4 w-4 text-gray-400" />
                                                <span>{info.seminar.location}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {info.expires_at && (
                                <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6">
                                    <Clock className="h-4 w-4 flex-shrink-0" />
                                    <span>Frist: {new Date(info.expires_at).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })} Uhr</span>
                                </div>
                            )}
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={doAccept}
                                    disabled={submitting}
                                    className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 transition-colors disabled:bg-gray-300"
                                    data-testid="waiting-list-accept-btn"
                                >
                                    <CheckCircle2 className="h-4 w-4" />
                                    {submitting ? 'Wird bestätigt…' : 'Platz annehmen'}
                                </button>
                                <button
                                    onClick={doDecline}
                                    disabled={submitting}
                                    className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-white border border-gray-200 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
                                    data-testid="waiting-list-decline-btn"
                                >
                                    <XCircle className="h-4 w-4" />
                                    Ablehnen
                                </button>
                            </div>
                        </>
                    ) : null}
                </div>
            </main>

            <footer className="bg-secondary py-6">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center text-xs text-gray-500">
                    Berliner Gesellschaft für Prävention und Rehabilitation von Herz-Kreislauferkrankungen e.V.
                </div>
            </footer>
        </div>
    );
}
